import { EditOutlined } from '@ant-design/icons'
import { Col, Divider, Flex, Grid, Row, Space, Typography } from 'antd'
import KItem from 'app/components/Modules/General/KItem'
import KAvatar from 'app/components/UI-Elements/DataDisplay/KAvatar'
import Button from 'app/components/UI-Elements/General/Button'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { RecordType } from 'app/constants/GlobalTypes'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { ToolBox } from 'app/lib/hooks/toolBox'
import useChangeEffect from 'app/lib/hooks/useChangeEffect'
import {
  useCurrentContact,
  useCurrentContactOrganization,
  useCurrentCustomer
} from 'app/lib/hooks/useCurrent'
import { useHelpUrl } from 'app/lib/hooks/useHelpUrl'
import useTranslation from 'app/lib/hooks/useTranslation'
import CapacitorService, { PlatformEnum } from 'app/lib/services/CapacitorService'
import { Routes } from 'app/routes/Routes'
import { useLocation, useNavigate } from 'react-router-dom'

import Logout from '../Account/Logout'
import LanguageToggle from '../Contact/LanguageToggle'
import ProfileForm from './Form'
import ProfileLink from './Link'
import Version from './Version'

type Props = {
  close?: () => void
}

const { isWeb, platform } = CapacitorService

const urls = {
  data_protection: 'https://www.klarx.de/impressum',
  imprint: {
    ios: 'https://www.klarx.de/datenschutz/ios',
    android: 'https://www.klarx.de/datenschutz/android',
    web: ''
  },
  helpCenterUrl: useHelpUrl()
}

const portalId = 'profile-settings-portal-id'

const ProfileContent: React.FC<Props> = ({ close }) => {
  const customer = useCurrentCustomer()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const contact = useCurrentContact()
  const contactOrganization = useCurrentContactOrganization()
  const { getInitials } = ToolBox.useString()

  const {
    drawerfy: { open: openDrawer, close: closeDrawer }
  } = useFeedbacker()
  const { tComponent, t } = useTranslation({ componentType: 'navigation', scope: 'sidebar' })

  const { md } = Grid.useBreakpoint()

  useChangeEffect(() => {
    close?.()
  }, [pathname])

  const translate = (id: string) => t({ id: `component.feedback.drawer.profile.${id}` })

  const openRoute = (route: string) => navigate(route)

  const onSettingsOpen = () => {
    openDrawer({
      content: (
        <ProfileForm
          portalId={portalId}
          onSuccess={() => closeDrawer()}
          cancelable
          onCancel={() => closeDrawer()}
        />
      ),
      recordType: RecordType.profile,
      action: 'update',
      width: 500,
      destroyOnClose: true,
      footer: <div id={portalId} />
    })
  }

  return (
    <>
      <Row gutter={[0, 12]}>
        <If condition={contact}>
          <Col span={24}>
            <KItem
              title={
                <Typography.Text className="text-capitalize text-base">
                  <h4 className={'mb-0 '}>{contact?.name} </h4>
                </Typography.Text>
              }
              description={
                <>
                  <Col span={24}>
                    <Typography.Text className="text-muted text-truncate text-lowercase">
                      {contact?.email}
                    </Typography.Text>
                  </Col>
                  <Col span={24} className="text-muted">
                    {t({ id: contactOrganization.role })}
                  </Col>
                </>
              }
              avatar={<KAvatar type="primary">{getInitials(contact?.name)}</KAvatar>}
            />
          </Col>
        </If>
        <Divider className={'my-0'} />
        <If condition={customer}>
          <Flex
            className="position-relative pointer full-width"
            vertical
            onClick={() => openRoute(Routes.Account.Base)}>
            <Flex justify="space-between">
              <div className="text-truncate text-capitalize h5 mb-0">{customer.organization}</div>
              <div>
                <Button
                  style={{
                    backgroundColor: 'unset',
                    border: 'none',
                    padding: 0,
                    width: '100%'
                  }}
                  size="small"
                  type="secondary"
                  icon={<EditOutlined />}
                />
              </div>
            </Flex>
            <div className="text-muted pb-2">{customer.id}</div>
          </Flex>
        </If>
      </Row>
      <Row>
        <KCol span={24}>
          <Row gutter={[0, 24]}>
            <If condition={!isWeb()}>
              <Col span={24}>
                <Divider className="my-0" />
              </Col>
              <Col span={24}>
                <Space direction="vertical">
                  <ProfileLink title={translate('imprint')} url={urls.data_protection} />
                  <If condition={urls.imprint[platform as PlatformEnum]}>
                    <ProfileLink
                      title={translate('data_protection')}
                      url={urls.imprint[platform as PlatformEnum]}
                    />
                  </If>
                  <ProfileLink title={translate('help_center')} url={urls.helpCenterUrl} />
                </Space>
              </Col>
            </If>
            <Divider className="my-0 " />
            <Row gutter={[0, 12]}>
              <Col
                span={24}
                className="on-hover-underline pointer"
                onClick={() => openRoute(Routes.FAQ.Base)}>
                {tComponent({ id: 'faq' })}
              </Col>
              <Col span={24} className="on-hover-underline pointer" onClick={onSettingsOpen}>
                {t({ id: 'userProfile.settings' })}
              </Col>
              <Col span={24}>
                <LanguageToggle />
              </Col>
              <Col span={24}>
                {!md && <Version />}
                <Logout />
              </Col>
            </Row>
          </Row>
        </KCol>
      </Row>
    </>
  )
}

export default ProfileContent
